import { Conditional } from "@result/rui/components";
import { classNames, toClassName } from "@result/rui/utils/component";
import { KEY_SPACE } from "@result/rui/utils/dom";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { NavLink, useLocation } from "react-router-dom";

import { IsAuthorized } from "#/components/Authorization";
import * as LTE from "#/def/lookup-tables.enums.gen";
import env from "#/env";
import useAuthorization from "#/hooks/useAuthorization";
import {
  MENU_STATE_COMPACT,
  MENU_STATE_EXPANDED,
  hasSidebarRail,
} from "#/layout/LeftSidebar";
import {
  MODULE_HORSES,
  MODULE_PIGS,
  MODULE_SMALL_LIVESTOCK,
  hasModuleAccess,
} from "#/utils/moduleAccess";
import { ADMIN_PANNEL_ACCESS_ROLES } from "#/def/adminRoles";

/** Matches landing-page staff tabs per species module. */
function getNonBreederRoles() {
  if (env.isHorses) {
    return [
      LTE.SifNivoDostopa.VF,
      LTE.SifNivoDostopa.KL,
      LTE.SifNivoDostopa.ADMIN,
      LTE.SifNivoDostopa.VETERINAR,
      LTE.SifNivoDostopa.KONTROLOR,
      LTE.SifNivoDostopa.SELEKCIONIST,
      LTE.SifNivoDostopa.VF_ANALITIK,
      LTE.SifNivoDostopa.RAZVIJALEC,
    ];
  }
  if (env.isPigs) {
    return [
      LTE.SifNivoDostopa.BF,
      LTE.SifNivoDostopa.ADMIN,
      LTE.SifNivoDostopa.SELEKCIONIST,
      LTE.SifNivoDostopa.BF_ANALITIK,
      LTE.SifNivoDostopa.RAZVIJALEC,
    ];
  }
  if (env.isSmallLivestock) {
    return [
      LTE.SifNivoDostopa.BF,
      LTE.SifNivoDostopa.ADMIN,
      LTE.SifNivoDostopa.KONTROLOR,
      LTE.SifNivoDostopa.SELEKCIONIST,
      LTE.SifNivoDostopa.BF_ANALITIK,
      LTE.SifNivoDostopa.RAZVIJALEC,
    ];
  }
  return [];
}

const MANAGING_ROLES = [
  LTE.SifNivoDostopa.ADMIN,
  LTE.SifNivoDostopa.BF,
  LTE.SifNivoDostopa.BF_ANALITIK,
  LTE.SifNivoDostopa.VF,
  LTE.SifNivoDostopa.VF_ANALITIK,
  LTE.SifNivoDostopa.RAZVIJALEC,
]

export default function MainNavigation({ menuState, setMenuState }) {
  const { t } = useTranslation();
  const isCompact = menuState === MENU_STATE_COMPACT;
  const auth = useAuthorization();

  const canAccessAdminPanel = ADMIN_PANNEL_ACCESS_ROLES.includes(auth?.full?.nivoDostopa);
  const canManage = MANAGING_ROLES.includes(auth?.full?.nivoDostopa);
  const canManageAssignments = canManage || auth.isKL;
  const nonBreederRoles = useMemo(() => getNonBreederRoles(), []);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = React.useRef(null);

  const moduleIcon = useMemo(() => {
    if (env.isPigs) {
      return "icon-pig";
    } else if (env.isHorses) {
      return "icon-horse";
    } else if (env.isSmallLivestock) {
      return "icon-sheep";
    } else if (env.isCattle) {
      // The merged cow+chart glyph, not plain `icon-cow` — that one belongs to
      // eGovedo, the separate application this module links out to.
      return "icon-cow-chart";
    }
    return "icon-circle-x";
  }, []);

  /**
   * Without the rail the menu is an overlay drawer — following a link has to
   * collapse it, otherwise it keeps covering the page it navigated to. Where
   * there is a rail the menu is part of the layout and must stay put.
   */
  const collapseAfterNavigation = (event) => {
    if (!event.target.closest("a")) {
      return;
    }
    if (!hasSidebarRail()) {
      setMenuState(MENU_STATE_COMPACT);
    }
  };

  /**
   * TODO: Refactor with using hook since this implementation is repeated 3 times
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <>
      <div
        ref={dropdownRef}
        className={classNames(
          "dropdown",
          "is-block",
          toClassName(dropdownOpen, "is-active")
        )}>
        <div className="dropdown-trigger">
          <button
            className="button main-navigation-header"
            aria-haspopup="true"
            aria-controls="dropdown-menu"
            onKeyUp={(e) =>
              e.key === KEY_SPACE && setDropdownOpen(!dropdownOpen)
            }
            onClick={() => setDropdownOpen(!dropdownOpen)}>
            <span
              className={classNames(
                "main-navigation-logo",
                "icon",
                "is-size-3",
                moduleIcon
              )}
            />
            {/*
             * Every other module labels itself with a wordmark image via
             * $logo-module. Cattle has no such asset, and reusing the eGovedo
             * one would name the wrong application — so it labels itself.
             */}
            <div className="main-navigation-logo-label">
              {env.isCattle && (
                <span className="main-navigation-logo-title">
                  {t("mainNavigation.cattle.appTitle")}
                </span>
              )}
            </div>
            <span className="main-navigation-dropdown-indicator icon icon-chevron-down" />
          </button>
        </div>
        <div className="dropdown-menu" id="dropdown-menu" role="menu">
          <div className="dropdown-content">
            <NavLink to={env.APP_URL_CATTLE} className="dropdown-item">
              <span className="icon is-species icon-cow is-medium has-text-cows" />
              <span className="dropdown-label">eGovedo</span>
              <span className="icon" />
            </NavLink>
            {/*
             * The cattle breeding-values app is a POKK module, unlike eGovedo
             * above it, so it carries the same active-state treatment as the
             * animal modules. Same brown as eGovedo — they are both cattle —
             * but a distinct glyph, since they are different applications.
             */}
            <NavLink
              to={env.APP_URL_GOVEDO_PV}
              className={classNames(
                "dropdown-item",
                toClassName(env.isCattle, "is-active")
              )}>
              <span className="icon is-species icon-cow-chart is-medium has-text-cows" />
              <span className="dropdown-label">
                {t("mainNavigation.cattle.appTitle")}
              </span>
              <span
                className={classNames(
                  "icon",
                  "is-success",
                  toClassName(env.isCattle, "icon-check")
                )}
              />
            </NavLink>
            <Conditional condition={hasModuleAccess(auth.full, MODULE_PIGS)}>
              <NavLink
                to={env.APP_URL_PIGS}
                className={classNames(
                  "dropdown-item",
                  toClassName(env.isPigs, "is-active")
                )}>
                <span className="icon is-species icon-pig is-medium has-text-pigs" />
                <span className="dropdown-label">ePrašiči</span>
                <span
                  className={classNames(
                    "icon",
                    "is-success",
                    toClassName(env.isPigs, "icon-check")
                  )}
                />
              </NavLink>
            </Conditional>
            <Conditional condition={hasModuleAccess(auth.full, MODULE_HORSES)}>
              <NavLink
                to={env.APP_URL_HORSES}
                className={classNames(
                  "dropdown-item",
                  toClassName(env.isHorses, "is-active")
                )}>
                <span className="icon is-species icon-horse is-medium has-text-horses" />
                <span className="dropdown-label">eKopitarji</span>
                <span
                  className={classNames(
                    "icon",
                    "is-success",
                    toClassName(env.isHorses, "icon-check")
                  )}
                />
              </NavLink>
            </Conditional>
            <Conditional
              condition={hasModuleAccess(auth.full, MODULE_SMALL_LIVESTOCK)}>
              <NavLink
                to={env.APP_URL_SMALL_LIVESTOCK}
                className={classNames(
                  "dropdown-item",
                  toClassName(env.isSmallLivestock, "is-active")
                )}>
                <span className="icon is-species icon-sheep is-medium has-text-small-livestock" />
                <span className="dropdown-label">eDrobnica</span>
                <span
                  className={classNames(
                    "icon",
                    "is-success",
                    toClassName(env.isSmallLivestock, "icon-check")
                  )}
                />
              </NavLink>
            </Conditional>
            <NavLink to={env.APP_URL_BEES} className="dropdown-item">
              <span className="icon is-species icon-bee is-medium has-text-bees" />
              <span className="dropdown-label">eČebele</span>
              <span className="icon" />
            </NavLink>
            <NavLink to={env.APP_URL_GENE_BANK} className="dropdown-item">
              <span className="icon is-species icon-gene-bank is-medium has-text-gene-bank" />
              <span className="dropdown-label">Genska banka</span>
              <span className="icon" />
            </NavLink>
          </div>
        </div>
      </div>

      <button
        type="button"
        className="main-navigation-toggle is-flex"
        onClick={() =>
          setMenuState(isCompact ? MENU_STATE_EXPANDED : MENU_STATE_COMPACT)
        }>
        <span
          className={classNames("is-size-4 icon", {
            "icon-openmenu": isCompact,
            "icon-closemenu": !isCompact,
          })}
        />
      </button>
      <ul className="main-navigation" onClick={collapseAfterNavigation}>

        {env.isCattle ? (
          <CattleNavigation />
        ) : (
          <>
        <li>
          <p className="menu-label">{t("animals")}</p>
          <ul>
            <Item
              label={t("drawer.animalOverview")}
              route="/"
              icon="clipboard-list"
            />
            <Item
              label={t("events.events")}
              route="/?tab=events"
              icon="clipboard-check"
            />
            <IsAuthorized as={nonBreederRoles}>
              <Item
                label={t("documentsAndPrintouts")}
                route="/?tab=documents"
                icon="file-text"
              />
              <Item
                label={t("analytics.analytics")}
                route="/?tab=analytics"
                icon="chart-line"
              />
              <Conditional condition={env.isHorses}>
                <Item
                  label={t("fieldwork.title")}
                  route="/?tab=fieldwork"
                  icon="car-front"
                />
              </Conditional>
            </IsAuthorized>
          </ul>
        </li>
        <Conditional
          condition={
            canManage || auth.isControlor || auth.isSelector || auth.isKL
          }>
          <li>
            <p className="menu-label">{t("mainNavigation.breeders.section")}</p>
            <ul>
              <Item
                label={t("mainNavigation.breeders.label")}
                route="/breeders"
                icon="users"
              />
              <Conditional condition={canManageAssignments}>
                <Item
                  label={t("mainNavigation.manage-assignments.label")}
                  route="/manage-assignments"
                  icon="user-plus"
                />
              </Conditional>
            </ul>
          </li>
        </Conditional>
        <li>
          <p className="menu-label">{t("mainNavigation.management.section")}</p>
          <ul>
            <Conditional condition={canManage}>
              <Item
                label={t("mainNavigation.reports.label")}
                route="/reports"
                icon="file-text"
              />
            </Conditional>
            <Conditional condition={ canAccessAdminPanel }>
              <Item
                label={t("mainNavigation.settings.label")}
                route="/admin"
                icon="gear"
              />
            </Conditional>
            <Item
              label={t("mainNavigation.profile.label")}
              route="/profile"
              icon="user"
            />
          </ul>
        </li>
        </>
        )}
      </ul>
      <div className="main-navigation-footer">
        <span className="main-navigation-footer-author">
          v{env.APP_VERSION}
        </span>
        <span className="main-navigation-footer-author">{env.APP_AUTHOR}</span>
        <Link
          to={env.APP_URL_LANDING_PAGE}
          className="main-navigation-footer-label"
        />
      </div>
    </>
  );
}

/**
 * The cattle module has no animal register of its own: the herd book lives in
 * eGovedo, a separate application outside this monorepo, and everything POKK
 * serves for cattle is the breeding values feature.
 */
function CattleNavigation() {
  const { t } = useTranslation();

  return (
    <>
      <li>
        <p className="menu-label">{t("mainNavigation.cattle.text")}</p>
        <ul>
          <Item
            label={t("breedingValues")}
            route="/breeding-values"
            icon="chart-line"
          />
          {/* Absolute URL — Item renders it as a plain link out of the app. */}
          <Item
            label={t("mainNavigation.cattle.label")}
            route={env.APP_URL_CATTLE}
            icon="cow"
          />
        </ul>
      </li>
      <li>
        <p className="menu-label">{t("mainNavigation.management.section")}</p>
        <ul>
          <Item
            label={t("mainNavigation.profile.label")}
            route="/profile"
            icon="user"
          />
        </ul>
      </li>
    </>
  );
}

function Item({ label, route, icon }) {
  const location = useLocation();
  const currentPath = location.pathname + location.search;
  const managementRoutes = [
    "/admin",
    "/reports",
    "/profile",
    "/breeders",
    "/manage-assignments",
  ];
  const isManagementPage = managementRoutes.some(
    (managementRoute) =>
      currentPath === managementRoute ||
      currentPath.startsWith(`${managementRoute}/`)
  );
  const isAbsoluteUrl = /^https?:\/\//.test(route);
  let isActive;
  if (isAbsoluteUrl) {
    const currentOrigin = window.location.origin;
    const targetOrigin = new URL(route).origin;
    isActive = currentOrigin === targetOrigin && !isManagementPage;
  } else {
    isActive = currentPath === route || currentPath.startsWith(`${route}/`);
  }
  return (
    <li className="is-flex has-text-white my-3">
      <NavLink
        to={route}
        className={classNames("menu-item-link", {
          "is-active": isActive,
        })}>
        <Conditional condition={icon}>
          <span className={`icon icon-${icon} is-size-5`} />
        </Conditional>
        <div className="menu-item-text">{label}</div>
      </NavLink>
    </li>
  );
}
